import React, { useState, useEffect, useContext } from 'react';
import { Container, Typography, Grid, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from '../axiosConfig';
import { ConfigLinksDetailsContext } from '../Context/cofingLinksDetailsContext';

const decodeHtml = (str) => {
    try {
        const txt = document.createElement('textarea');
        txt.innerHTML = str;
        return txt.value;
    } catch (_) { return str; }
};

const getExcerpt = (htmlText, maxLength = 180) => {
    const clean = (htmlText || '').replace(/<[^>]*>/g, ' ');
    const plain = decodeHtml(clean).replace(/\s+/g, ' ').trim();
    if (!plain) return '';
    return plain.length > maxLength ? `${plain.slice(0, maxLength).trim()}...` : plain;
};

const AR_MONTHS = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
const EN_MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const formatDate = (dateStr, isRTL) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const day = String(d.getDate()).padStart(2, '0');
    const month = (isRTL ? AR_MONTHS : EN_MONTHS)[d.getMonth()];
    return `${day} ${month} ${d.getFullYear()}`;
};

const pick = (ar, en, isRTL) => (isRTL ? ar || en : en || ar) || '';

const NewsCard = ({ item, isRTL, onRead, readMoreLabel }) => {
    const dateStr = formatDate(item.publishDate, isRTL);
    const title   = pick(item.articleTitleAr, item.articleTitleEn, isRTL) || item.articleTitle || '';
    const text    = pick(item.articleTextAr,  item.articleTextEn,  isRTL) || item.articleText  || '';
    return (
        <Box
            onClick={() => onRead(item.articleId)}
            sx={{
                display: 'flex',
                flexDirection: 'row',
                border: '1px solid #e8ecf0',
                borderRadius: 2,
                overflow: 'hidden',
                cursor: 'pointer',
                backgroundColor: '#ffffff',
                width: '100%',
                transition: 'box-shadow 0.2s, transform 0.2s',
                '&:hover': { boxShadow: '0 4px 18px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' },
                height: '100%',
            }}
        >
            {/* Image on the right in RTL = first in DOM */}
            {item.coverImageUrl && (
                <Box
                    component='img'
                    src={item.coverImageUrl}
                    alt={title}
                    sx={{
                        width: { xs: 120, sm: 150, md: 175 },
                        flexShrink: 0,
                        objectFit: 'cover',
                    }}
                />
            )}

            {/* Text Block */}
            <Box dir={isRTL ? 'rtl' : 'ltr'} sx={{ flex: 1, minWidth: 0, p: { xs: 1.5, md: 2 }, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {dateStr && (
                    <Typography variant='caption' sx={{ color: 'text.disabled', fontWeight: 500 }}>
                        {dateStr}
                    </Typography>
                )}
                <Typography
                    variant='subtitle1'
                    fontWeight='bold'
                    sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        lineHeight: 1.5,
                        mb: 0.5,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant='body2'
                    sx={{
                        color: '#64748b',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        flex: 1,
                        lineHeight: 1.7,
                    }}
                >
                    {getExcerpt(text)}
                </Typography>
                <Typography
                    variant='body2'
                    color='primary.main'
                    sx={{ mt: 0.5, fontWeight: 500 }}
                >
                    {readMoreLabel} ...
                </Typography>
            </Box>
        </Box>
    );
};

const News = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { configLinks, isConfigLoaded } = useContext(ConfigLinksDetailsContext);
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const isRTL = i18n.language?.startsWith('ar');
    const newsCategoryId = Number(configLinks?.newsCatId);

    useEffect(() => {
        if (!isConfigLoaded) return;

        if (!(newsCategoryId > 0)) {
            setNews([]);
            setLoading(false);
            return;
        }

        let isCancelled = false;

        const fetchNews = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`/CmsArticle/${newsCategoryId}`);
                let articles = Array.isArray(response) ? response : (response?.dataList || []);
                const excludeIds = [configLinks?.tcArticleId, configLinks?.policyArticleId].filter(Boolean);
                if (excludeIds.length > 0) articles = articles.filter(a => !excludeIds.includes(a.articleId));
                if (!isCancelled) {
                    setNews(articles);
                }
            } catch (error) {
                console.error('Error fetching news:', error);
                if (!isCancelled) {
                    setNews([]);
                }
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                }
            }
        };

        fetchNews();

        return () => {
            isCancelled = true;
        };
    }, [configLinks?.policyArticleId, configLinks?.tcArticleId, isConfigLoaded, newsCategoryId]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;

    return (
        <Container maxWidth='xl' sx={{ py: 5, minHeight: '60vh' }} dir={isRTL ? 'rtl' : 'ltr'}>
            <Typography variant='h4' fontWeight='bold' mb={1} color='primary.main'>
                {t('nav_news', 'الأخبار')}
            </Typography>
            <Typography variant='body2' color='text.secondary' mb={4}>
                {t('news_subtitle', 'للاطلاع على اخر الاخبار')}
            </Typography>
            {news.length === 0 ? (
                <Typography>{t('no_news_found', 'لا يوجد أخبار حالياً')}</Typography>
            ) : (
                (() => {
                    const mdCols = news.length >= 4 ? 3 : Math.floor(12 / news.length);
                    const smCols = news.length >= 2 ? 6 : 12;
                    return (
                        <Grid container spacing={2}>
                            {news.map((item) => (
                                <Grid item xs={12} sm={smCols} md={mdCols} key={item.articleId} sx={{ display: 'flex' }}>
                                    <NewsCard
                                        item={item}
                                        isRTL={isRTL}
                                        onRead={(id) => navigate('/news/' + id)}
                                        readMoreLabel={t('read_more', 'اقرأ المزيد')}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    );
                })()
            )}
        </Container>
    );
};

export default News;
